import {
  ProgramEditFormikModel,
  ProgramScheduledType,
  ProgramScheduledTypeOptions,
} from '../../types';
import { useIsSmallerThanTablet } from '../../../responsive/hooks/hooks';
import React from 'react';
import { displayEndDateToInternal, internalEndDateToDisplay } from '../../../util/date-utils';
import { Form } from 'react-bootstrap';
import { isScheduled, isUnscheduled } from '../../unscheduledProgramUtil';
import { ColoredSelect } from '../ColoredSelect';
import {
  isSocialPostingProgramType,
  makeProgramSchedule,
  ModalDropdownMenuZIndex,
  ProgramSchedule,
} from '../../util';
import DatePicker from 'react-datepicker';
import clsx from 'clsx';
import { DivButton } from '../../../commons/components/DivButton';
import { CloseIcon, DividerIcon } from '../../../assets/icons';
import { recalculateDueDates } from '../tasks/smartDueDateUtils';

export const DatesContainer = (props: {
  wholeDay: boolean;
  isSocialPosting: boolean;
  disabled: boolean;
  readOnly: boolean;
  startDateTime: Date;
  onStartChange: (date: Date | [Date, Date] | null) => void;
  startInvalid: boolean;
  endDateTime: Date;
  onEndChange: (date: Date | [Date, Date] | null) => void;
  endInvalid: boolean;
  onWholeDayChange: () => void;
  onUnscheduledClick: () => void;
}) => {
  const isSmallerThanTablet = useIsSmallerThanTablet();

  const {
    wholeDay,
    isSocialPosting,
    disabled,
    readOnly,
    startDateTime,
    onStartChange,
    startInvalid,
    endDateTime,
    onEndChange,
    endInvalid,
    onWholeDayChange,
    onUnscheduledClick,
  } = props;
  return (
    <div className="program-edit__dates-container">
      <div className="program-edit__dates-row-container">
        <div className="program-edit__input--min-sized" data-test="program-edit__start-date">
          <DatePicker
            showTimeSelect={!wholeDay}
            timeIntervals={15}
            dateFormat={wholeDay ? 'MMM dd' : 'MMM dd, hh:mm a'}
            selected={(startDateTime && new Date(startDateTime)) || null}
            disabled={disabled}
            readOnly={readOnly}
            onChange={onStartChange}
            className={clsx(
              'growegy-form__input',
              isSmallerThanTablet
                ? 'program-edit__input-redesigned--sized-small'
                : 'program-edit__input-redesigned--sized',
              {
                'is-invalid': startInvalid,
              }
            )}
          />
        </div>
        {!isSocialPosting && (
          <>
            {isSmallerThanTablet ? (
              <span>&nbsp;</span>
            ) : (
              <div className="program-edit__dates-to-container">
                <span>to</span>
              </div>
            )}
            <div className="program-edit__input--min-sized" data-test="program-edit__end-date">
              <DatePicker
                showTimeSelect={!wholeDay}
                timeIntervals={15}
                dateFormat={wholeDay ? 'MMM dd' : 'MMM dd, hh:mm a'}
                selected={
                  (endDateTime && internalEndDateToDisplay(new Date(endDateTime), wholeDay)) || null
                }
                disabled={disabled}
                readOnly={readOnly}
                onChange={onEndChange}
                minDate={(startDateTime && new Date(startDateTime)) || null}
                className={clsx(
                  'growegy-form__input',
                  isSmallerThanTablet
                    ? 'program-edit__input-redesigned--sized-small'
                    : 'program-edit__input-redesigned--sized',
                  {
                    'is-invalid': endInvalid,
                  }
                )}
              />
            </div>
          </>
        )}
        {!disabled && !readOnly && (
          <div style={{ marginLeft: isSmallerThanTablet ? '4px' : '8px' }} className="d-flex">
            {!isSmallerThanTablet && !isSocialPosting && (
              <>
                <DivButton
                  isGray={true}
                  text={wholeDay ? 'Add time' : 'Remove time'}
                  dataTest={wholeDay ? 'program-edit__add-time' : 'program-edit__remove-time'}
                  onClick={onWholeDayChange}
                />
                <div style={{ margin: '0 16px' }}>
                  <DividerIcon />
                </div>
              </>
            )}
            <DivButton
              isGray={true}
              text={
                isSmallerThanTablet
                  ? undefined
                  : !isSocialPosting
                  ? 'Remove dates'
                  : 'Remove schedule'
              }
              icon={isSmallerThanTablet ? <CloseIcon /> : undefined}
              dataTest="program-edit__unscheduled"
              onClick={onUnscheduledClick}
            />
          </div>
        )}
      </div>
      {isSocialPosting && <span className="program-edit__dates-note">Time is local</span>}
    </div>
  );
};

export const ProgramEditDates = ({
  initialValues,
  values,
  disabled,
  readOnly,
  isStartInvalid,
  isEndInvalid,
  setFieldValue,
  setFieldTouched,
}: {
  initialValues: ProgramEditFormikModel;
  values: ProgramEditFormikModel;
  disabled: boolean;
  readOnly: boolean;
  isStartInvalid: boolean;
  isEndInvalid: boolean;
  setFieldValue: (
    field: keyof ProgramEditFormikModel,
    value: unknown,
    shouldValidate?: boolean | undefined
  ) => void;
  setFieldTouched: (
    field: keyof ProgramEditFormikModel,
    isTouched?: boolean | undefined,
    shouldValidate?: boolean | undefined
  ) => void;
}) => {
  const isSocialPosting = isSocialPostingProgramType(values.type);
  const recalculateTaskDueDates = (programSchedule: ProgramSchedule) => {
    const updatedTasks = recalculateDueDates(programSchedule, values.resolvedTasks, false);
    if (updatedTasks) {
      setFieldValue('resolvedTasks', updatedTasks, true);
      setFieldTouched('resolvedTasks', true, true);
    }
  };

  const applyNewProgramSchedule = (programSchedule: ProgramSchedule) => {
    setFieldValue('startDateTime', programSchedule.startDateTime);
    setFieldValue('endDateTime', programSchedule.endDateTime);
    setFieldValue('wholeDay', programSchedule.wholeDay);
    setFieldTouched('startDateTime', true, true);
    setFieldTouched('endDateTime', true, true);
    setFieldTouched('wholeDay', true, true);
    recalculateTaskDueDates(programSchedule);
  };

  const onScheduledClick = () => {
    if (!isUnscheduled(values)) return;

    applyNewProgramSchedule(
      isScheduled(initialValues)
        ? initialValues
        : makeProgramSchedule({ type: 'SCHEDULED', wholeDay: true, isSocialPosting })
    );
  };

  const onUnscheduledClick = () => {
    if (!isScheduled(values)) return;

    applyNewProgramSchedule(
      makeProgramSchedule({
        type: 'UNSCHEDULED',
        programId: initialValues.id,
      })
    );
  };

  const onWholeDayChange = (isWholeDay: boolean) => {
    applyNewProgramSchedule(
      makeProgramSchedule({
        type: 'SCHEDULED',
        rangeStart: values.startDateTime,
        rangeEnd: isWholeDay
          ? values.endDateTime
          : internalEndDateToDisplay(values.endDateTime, true),
        wholeDay: isWholeDay,
        isSocialPosting,
      })
    );
  };

  const onStartChange = (d: Date | [Date, Date] | null) => {
    if (!(d instanceof Date)) return;
    applyNewProgramSchedule(
      makeProgramSchedule({
        type: 'SCHEDULED',
        rangeStart: d,
        rangeEnd: values.endDateTime,
        wholeDay: values.wholeDay,
        isSocialPosting,
      })
    );
  };

  const onEndChange = (d: Date | [Date, Date] | null) => {
    if (!(d instanceof Date)) return;
    const adjustedEnd = displayEndDateToInternal(d, values.wholeDay);
    applyNewProgramSchedule(
      makeProgramSchedule({
        type: 'SCHEDULED',
        rangeStart: values.startDateTime,
        rangeEnd: adjustedEnd,
        wholeDay: values.wholeDay,
        isSocialPosting,
      })
    );
  };

  return (
    <Form.Group controlId="startDateTime" className="growegy-form__row">
      <div className="d-flex align-items-baseline">
        <label className="growegy-form__label">{isSocialPosting ? 'Publish on' : 'Dates'}</label>
      </div>
      <>
        {isUnscheduled(values) ? (
          <div>
            <ColoredSelect
              initialValue={
                ProgramScheduledTypeOptions.find(
                  (o) => (o.id as ProgramScheduledType) === 'Unscheduled'
                )!
              }
              coloredItems={[...ProgramScheduledTypeOptions]}
              onValueChange={(value) => {
                if ((value.id as ProgramScheduledType) === 'Scheduled') {
                  onScheduledClick();
                }
              }}
              dataTestPrefix="program-scheduled-options"
              disabled={disabled || readOnly}
              menuZIndex={ModalDropdownMenuZIndex}
            />
          </div>
        ) : (
          <DatesContainer
            wholeDay={values.wholeDay && !isSocialPosting}
            isSocialPosting={isSocialPosting}
            disabled={disabled}
            readOnly={readOnly}
            startDateTime={values.startDateTime}
            onStartChange={onStartChange}
            startInvalid={isStartInvalid}
            endDateTime={values.endDateTime}
            onEndChange={onEndChange}
            endInvalid={isEndInvalid}
            onWholeDayChange={() => onWholeDayChange(!values.wholeDay)}
            onUnscheduledClick={() => onUnscheduledClick()}
          />
        )}
      </>
    </Form.Group>
  );
};
